import { useState } from "react";

interface PillSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
  onClearError?: () => void;
  options: string[];
}

export default function PillSelect({
  label,
  value,
  onChange,
  error,
  errorMessage,
  onClearError,
  options,
}: PillSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.length > 0;
  const isFloating = isFocused || hasValue || isOpen;

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setIsFocused(!isFocused);
  };

  const handleSelect = (option: string) => {
    if (error && onClearError) {
      onClearError();
    }
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={`relative rounded-[56px] border border-solid bg-white ${error ? "border-red-500" : "border-[#d3d8db]"} flex h-[56px] cursor-pointer items-center`}
        onClick={toggleDropdown}
      >
        <div className="relative h-full flex-1">
          <label
            className={`pointer-events-none absolute font-['Inter'] transition-all duration-200 ease-out ${
              isFloating
                ? "left-[24px] top-[7px] text-[12px] text-[#1d2124]"
                : "left-[24px] top-[16px] text-[16px] text-[#1d2124]"
            }`}
          >
            {label}
          </label>
          <span
            className={`pl-[24px] font-['Inter'] text-[16px] font-normal leading-[1.2] transition-all duration-200 ease-out ${isFloating ? "pt-[24px]" : "py-[16px]"} ${value ? "block text-[#1d2124] opacity-100" : "opacity-0"}`}
          >
            {value}
          </span>
        </div>
        <svg
          className={`mr-[24px] size-[24px] transition-transform ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="#818a8f"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-[16px] border border-solid border-[#d3d8db] bg-white shadow-lg">
          {options.map((option) => (
            <div
              key={option}
              className="cursor-pointer px-[24px] py-[16px] font-['Inter'] text-[16px] font-normal leading-[1.2] text-[#1d2124] first:rounded-t-[16px] last:rounded-b-[16px] hover:bg-[#f5f5f5]"
              onClick={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
      {error && errorMessage && (
        <p className="mt-[8px] text-[14px] font-normal leading-[1.4] text-[#dc2626]">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
